/**
 * 后端统一响应信封 —— 与 {@code com.saaserp.common.api.R} 一一对应。
 *
 * 🔴 `code === 0` 才是成功。**HTTP 200 不代表成功**：
 * `GlobalExceptionHandler` 把绝大多数业务失败回成 `HTTP 200 + code != 0`
 * （只有越权 403 / 未登录 401 / 未预期异常 500 走 HTTP 状态码）。
 * ⛔ 只看 `response.ok` 的客户端会把每一个业务失败都当成成功。
 */
export interface ApiEnvelope<T> {
  code: number
  message: string
  data: T | null
  traceId: string | null
}

/** 成功码。与后端 `R.OK` 一致。 */
export const CODE_OK = 0

/**
 * 后端错误码里前端**确实需要认得出来**的几个。
 *
 * ⚠️ 这里只放「前端要据此决定挂哪个动作按钮」的码，**不放文案**。
 * 文案一律用后端下发的 `message`（见 erp-web/README §四）。
 */
export const ErrorCodes = {
  /** 99002 未登录或登录已过期 */
  UNAUTHORIZED: 99002,
  /** 99003 无权访问该资源（越权一律 403 + 这个码） */
  FORBIDDEN: 99003,
  /** 99004 租户上下文缺失 —— 开发期最常见的原因是没带 X-Tenant-Id */
  TENANT_CONTEXT_MISSING: 99004,
  /** 99001 参数不合法 */
  PARAM_INVALID: 99001,
  /** 18001 / HTTP409: original listing confirmation was not accepted; preview must be refreshed. */
  LISTING_PREVIEW_STALE: 18001,
  /** 12001 该店铺已被绑定 */
  SHOP_ALREADY_BOUND: 12001,
  /** 12010 店铺数量已达账号上限 */
  SHOP_QUOTA_EXCEEDED: 12010,
  /** 12011 暂时无法完成凭据校验，请稍后重试 —— 🔴 唯一「重试真的有意义」的一档 */
  SHOP_CREDENTIAL_CHECK_UNAVAILABLE: 12011,
  /** 12013 店铺凭据无法读取，请重新绑定店铺 */
  SHOP_CREDENTIAL_UNREADABLE: 12013,
  /** 12015 该店铺需要重新绑定 */
  SHOP_REBIND_REQUIRED: 12015,
  SHOP_AUTHORIZATION_PILOT_DISABLED: 12016,
  /** 12022 店铺凭据无效或已失效 */
  CHANNEL_AUTH_INVALID: 12022,
  /** 12023 店铺凭据缺少所需权限 —— ⚠️ 与 12022 处置完全不同，绝不能合并显示 */
  CHANNEL_AUTH_INSUFFICIENT_ROLE: 12023,

  // ---- 33xxx 人工入账通道（资金域）----
  // 🔴 这一段每一个码都在说「**这笔人工入账我们不给它入**」。
  //    它们的处置方向两两不同，⛔ 合并显示 = 让人对着一笔真金白银做错误动作。

  /** 33001 充值单不存在（与「不属于这个账号」**共用**这一个码，两者响应必须一模一样） */
  RECHARGE_ORDER_NOT_FOUND: 33001,
  /** 33002 入账金额与充值单金额不一致，拒绝入账 */
  RECHARGE_AMOUNT_MISMATCH: 33002,
  /** 33003 充值单当前状态不允许该操作 —— 结论是**确定的**，重试一万次都一样 */
  RECHARGE_ORDER_STATE_CONFLICT: 33003,
  /** 33004 金额币种不是记账币种，拒绝入账 —— ⚠️ 处置是**换一种付款方式**，不是改数字 */
  LEDGER_CURRENCY_MISMATCH: 33004,
  /** 33005 该账号还没有开通钱包（🔴 只用于**入账侧**；卖家自己看余额时「没开户」是正常状态） */
  ACCOUNT_NOT_OPENED: 33005,
  /**
   * 🔴🔴 33006 这笔人工入账**结果未定** —— 处置是**先查再决定**，不是重试。
   * 口径同 `ASYNC_TASK_TIMEOUT`：**TIMEOUT ≠ FAILED**。
   */
  RECHARGE_POSTING_UNRESOLVED: 33006,
  /**
   * 🔴🔴 33007 这笔收款流水**已经入过账了**，本次未入账（整笔已回滚）。
   *
   * ⛔ **绝不许并进「操作失败，请重试」那一档** —— 再点一次**永远不会成功**，
   * 而那正好会把它变成一次无穷重试。处置是**停下来**去核对。
   */
  RECHARGE_RECEIPT_ALREADY_POSTED: 33007,
} as const
